import React, { useCallback, useMemo, useState } from 'react';

import { createRoot } from 'react-dom/client';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { Shopify, ToriutLogo } from '@picsio/icons';
import { Button, Icon as PicsioIcon, InputControlLabel } from '@picsio/ui';

import { getChannelEntryId } from '../../store/products/helpers/channelHelpers';
import { resolveChannelLabel } from '../../store/products/helpers/showPublishedToChannelToast';
import {
  buildListingVariantMap,
  getChannelEntryHasLive,
  getChannelEntryListingProductId,
  getListedChannelEntries,
  getListingVariantsForChannel,
} from '../../store/selectors/productSelectors';
import l10n from '../../shared/strings';
import Checkbox from '../../UIComponents/checkbox';
import Icon from '../Icon';
import Tooltip from '../Tooltip';
import { resolveListingAttachMode } from './helpers/resolveListingAttachMode';
import styles from './styles.module.scss';

const CHANNEL_ICON_COLORS = {
  draft: '#FF6B00',
  publish: '#B6FA18',
};

function getMasterBrandIcon() {
  return (
    <PicsioIcon size="md" color="primary" className={styles.channelBrandIcon}>
      <ToriutLogo />
    </PicsioIcon>
  );
}

function getChannelBrandIcon(mode) {
  /** @TODO: After we add more channels, we need to add more icons here */
  return (
    <PicsioIcon
      size="md"
      className={styles.channelBrandIcon}
      style={{ color: CHANNEL_ICON_COLORS[mode] || CHANNEL_ICON_COLORS.draft }}
    >
      <Shopify />
    </PicsioIcon>
  );
}

function AssetTargetsDialog({
  product,
  productsState = null,
  multipleAssetsSelected,
  salesChannels = [],
  onConfirm,
  onCancel,
  root,
}) {
  const listingChannels = useMemo(
    () => getListedChannelEntries(product, productsState),
    [product, productsState],
  );
  const listingVariantsByChannel = useMemo(
    () => buildListingVariantMap(product, listingChannels, productsState),
    [product, listingChannels, productsState],
  );

  const [masterGallery, setMasterGallery] = useState(false);
  const [masterVariantChecks, setMasterVariantChecks] = useState(() => (
    (product.variants || []).reduce((acc, variant) => ({
      ...acc,
      [variant._id]: false,
    }), {})
  ));
  const [listingChecks, setListingChecks] = useState(() => (
    listingChannels.reduce((acc, channel) => {
      const channelId = getChannelEntryId(channel);
      if (!channelId) return acc;

      return {
        ...acc,
        [channelId]: {
          gallery: false,
          mode: 'draft',
          variantIds: getListingVariantsForChannel(product, channelId, productsState).reduce((vAcc, listingVariant) => ({
            ...vAcc,
            [listingVariant._id]: false,
          }), {}),
        },
      };
    }, {})
  ));

  const strings = l10n.DIALOGS.ASSET_TARGETS;

  const destroy = useCallback(() => {
    try {
      root.unmount();
    } catch { /* ignore */ }
  }, [root]);

  const handleCancel = useCallback(() => {
    destroy();
    onCancel?.();
  }, [destroy, onCancel]);

  const masterVariantIds = useMemo(
    () => Object.entries(masterVariantChecks).filter(([, v]) => v).map(([id]) => id),
    [masterVariantChecks],
  );

  const hasSelection = useMemo(() => {
    if (masterGallery) return true;
    if (masterVariantIds.length) return true;
    return listingChannels.some((channel) => {
      const channelId = getChannelEntryId(channel);
      const state = listingChecks[channelId];
      if (!state) return false;
      if (state.gallery) return true;
      return Object.values(state.variantIds || {}).some(Boolean);
    });
  }, [masterGallery, masterVariantIds, listingChannels, listingChecks]);

  const handleConfirm = useCallback(() => {
    const listings = listingChannels.reduce((acc, channel) => {
      const channelId = getChannelEntryId(channel);
      const state = listingChecks[channelId];
      if (!state) return acc;

      const listingVariantIds = Object.entries(state.variantIds || {})
        .filter(([, checked]) => checked)
        .map(([id]) => id);

      if (!state.gallery && !listingVariantIds.length) return acc;

      const hasLive = getChannelEntryHasLive(channel);
      acc.push({
        channelId,
        listingId: getChannelEntryListingProductId(channel),
        mode: resolveListingAttachMode(hasLive, state.mode),
        listingGallery: state.gallery,
        listingVariantIds,
      });
      return acc;
    }, []);

    destroy();
    onConfirm({
      masterGallery,
      masterVariantIds,
      listings,
    });
  }, [destroy, listingChannels, listingChecks, masterGallery, masterVariantIds, onConfirm]);

  const handleListingGalleryToggle = useCallback((channelId) => {
    setListingChecks((prev) => {
      const current = prev[channelId];
      const nextGallery = !current?.gallery;
      return {
        ...prev,
        [channelId]: {
          ...current,
          gallery: nextGallery,
          variantIds: nextGallery
            ? (current?.variantIds || {})
            : Object.keys(current?.variantIds || {}).reduce(
              (acc, id) => ({ ...acc, [id]: false }),
              {},
            ),
        },
      };
    });
  }, []);

  const handleListingModeChange = useCallback((channelId, nextMode) => {
    setListingChecks((prev) => {
      const current = prev[channelId];
      if (!current || current.mode === nextMode) return prev;

      return {
        ...prev,
        [channelId]: {
          ...current,
          mode: nextMode,
          gallery: false,
          variantIds: Object.keys(current.variantIds || {}).reduce(
            (acc, id) => ({ ...acc, [id]: false }),
            {},
          ),
        },
      };
    });
  }, []);

  const handleListingVariantToggle = useCallback((channelId, variantId) => {
    setListingChecks((prev) => {
      const current = prev[channelId];
      const nextChecked = !current?.variantIds?.[variantId];
      return {
        ...prev,
        [channelId]: {
          ...current,
          gallery: nextChecked ? true : current?.gallery,
          variantIds: {
            ...current?.variantIds,
            [variantId]: nextChecked,
          },
        },
      };
    });
  }, []);

  return (
    <div className={`simpleDialog ${styles.modal}`}>
      <div className="simpleDialogUnderlayer" />
      <div className="simpleDialogBox">
        <div className="simpleDialogHeader">
          <span className="simpleDialogTitle">
            {strings.TITLE_ATTACH}
          </span>
          <span
            role="button"
            tabIndex={0}
            className="simpleDialogBtnCross"
            onClick={handleCancel}
            onKeyDown={handleCancel}
          >
            <Icon name="close" />
          </span>
        </div>
        <div className="simpleDialogContent">
          <div className="simpleDialogContentInner">
            <p className={styles.subtitle}>{strings.SUBTITLE()}</p>

            <div className={styles.section}>
              <div className={styles.sectionHeader}>
                <div className={styles.sectionTitle}>
                  <span className={styles.sectionIcon}>
                    {getMasterBrandIcon()}
                  </span>
                  <span className={styles.sectionName}>{product.title}</span>
                </div>
                <span className={styles.masterBadge}>{strings.MASTER_BADGE}</span>
              </div>

              <div className={styles.sectionBody}>
                <Checkbox
                  className="w-100 m-b-8 c-d"
                  value={masterGallery}
                  label={strings.MASTER_GALLERY}
                  onChange={() => setMasterGallery((v) => !v)}
                />

                <If condition={(product.variants || []).length}>
                  <div className={styles.nested}>
                    {(product.variants || []).map((variant) => (
                      <Checkbox
                        key={variant._id}
                        className="m-b-8"
                        value={masterVariantChecks[variant._id]}
                        label={variant.title}
                        onChange={() => setMasterVariantChecks((prev) => ({
                          ...prev,
                          [variant._id]: !prev[variant._id],
                        }))}
                      />
                    ))}
                  </div>
                </If>
              </div>
            </div>

            {listingChannels.map((channel) => {
              const channelId = getChannelEntryId(channel);
              const channelState = listingChecks[channelId] || { gallery: false, mode: 'draft', variantIds: {} };
              const listingVariants = listingVariantsByChannel[channelId] || [];
              const channelLabel = resolveChannelLabel(channelId, salesChannels);
              const channelIcon = getChannelBrandIcon(channelState.mode);
              const hasLive = getChannelEntryHasLive(channel);
              const publishModeDisabled = !hasLive;
              const publishModeRadio = (
                <InputControlLabel
                  id={`listing-mode-${channelId}-publish`}
                  name={`listing-mode-${channelId}`}
                  value={channelState.mode === 'publish'}
                  label={strings.MODE_PUBLISH}
                  control="radio"
                  disabled={publishModeDisabled}
                  onChange={() => handleListingModeChange(channelId, 'publish')}
                />
              );

              return (
                <div key={channelId} className={styles.section}>
                  <div className={styles.sectionHeader}>
                    <div className={styles.sectionTitle}>
                      <If condition={channelIcon}>
                        {channelIcon}
                      </If>
                      <span className={styles.sectionName}>{product.title}</span>
                    </div>
                    <div className={styles.modeRow}>
                      <InputControlLabel
                        id={`listing-mode-${channelId}-draft`}
                        name={`listing-mode-${channelId}`}
                        value={channelState.mode === 'draft'}
                        label={strings.MODE_DRAFT}
                        control="radio"
                        onChange={() => handleListingModeChange(channelId, 'draft')}
                      />
                      <Choose>
                        <When condition={publishModeDisabled}>
                          <Tooltip content={strings.MODE_PUBLISH_DISABLED_TOOLTIP}>
                            {publishModeRadio}
                          </Tooltip>
                        </When>
                        <Otherwise>
                          {publishModeRadio}
                        </Otherwise>
                      </Choose>
                    </div>
                  </div>
                  <div className={styles.sectionBody}>
                    <Checkbox
                      className="m-b-8"
                      value={channelState.gallery}
                      label={strings.LISTING_GALLERY(channelLabel)}
                      onChange={() => handleListingGalleryToggle(channelId)}
                    />
                    <If condition={listingVariants.length}>
                      <div className={cn(styles.nested, styles.listingVariants)}>
                        {listingVariants.map((variant) => {
                          const disabled = multipleAssetsSelected;
                          const checkbox = (
                            <Checkbox
                              key={variant._id}
                              value={channelState.variantIds?.[variant._id]}
                              label={variant.title}
                              disabled={disabled}
                              onChange={() => handleListingVariantToggle(channelId, variant._id)}
                            />
                          );

                          if (!disabled) return checkbox;
                          return (
                            <Tooltip key={variant._id} content={strings.LISTING_VARIANT_ONE_ASSET}>
                              {checkbox}
                            </Tooltip>
                          );
                        })}
                      </div>
                    </If>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="simpleDialogFooter">
          <Button color="secondary" size="md" variant="contained" onClick={handleCancel}>
            {strings.BTN_CANCEL}
          </Button>
          <Tooltip content={!hasSelection ? strings.SELECT_TARGET_TOOLTIP : null}>
            <Button
              color="primary"
              size="md"
              variant="contained"
              disabled={!hasSelection}
              onClick={handleConfirm}
            >
              {strings.BTN_ATTACH}
            </Button>
          </Tooltip>
        </div>
      </div>
    </div>
  );
}

AssetTargetsDialog.propTypes = {
  product: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    title: PropTypes.string,
    variants: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  productsState: PropTypes.shape({}),
  multipleAssetsSelected: PropTypes.bool,
  salesChannels: PropTypes.arrayOf(PropTypes.shape({})),
  root: PropTypes.shape({ unmount: PropTypes.func.isRequired }).isRequired,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func,
};

AssetTargetsDialog.defaultProps = {
  productsState: null,
  multipleAssetsSelected: false,
  salesChannels: [],
  onCancel: undefined,
};

export function showAssetTargetsDialog(options) {
  const $wrapper = document.querySelector('.wrapperDialog');
  const root = createRoot($wrapper);
  root.render(
    <AssetTargetsDialog
      {...options}
      root={root}
    />,
  );
}

export default AssetTargetsDialog;
